import _ from 'lodash';
import pluralize from 'pluralize';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  ClientLink,
  Currency,
  DateTime,
  Table as DetailTable,
  ExportDropdown,
  ProjectLink,
  RouteLink,
  Tag,
  Tooltip,
  YesNoFilter,
} from '~/components';
import {
  ClientFiltersBar,
  ClientFiltersGroup,
  CurrencyFilter,
  FilterButton,
  InvoiceFiltersBar,
  InvoiceFiltersGroup,
  ProjectFiltersBar,
  ProjectFiltersGroup,
  ReportPeriodFilter,
} from '~/components/filters/index.js';
import { useApi, useConfirmation, useWorkspace } from '~/contexts';
import { useDocumentTitle, useFeatures, useSearchParams, useSearchParamsConfig } from '~/hooks';
import useClientFilters from '~/hooks/useClientFilters';
import useProjectFilters from '~/hooks/useProjectFilters';
import InvoiceSyncIndicator from '~/routes/app/invoices/components/InvoiceSyncIndicator';
import { PageLoader } from '~/routes/public/pages';
import { colors, weights } from '~/styles';
import { QuerySort, intervalOptions } from '~/utils';
import mimeTypes from '~/utils/mimeTypes';
import SentIndicator from '../../invoices/components/SentIndicator';
import ExportDialogAsync from '../components/ExportDialogAsync';
import Report from '../components/Report';
import Table from '../components/table';
import useReportsSearchParamsConfig from '../hooks/useReportsSearchParamsConfig';
import SavedReportActions from '../ReportActions';

const Byline = styled.small`
  display: block;
`;

export default function PaymentsByInvoice({ report }) {
  useDocumentTitle(report.name);
  const { workspace } = useWorkspace();
  const api = useApi();
  const features = useFeatures();

  const [query, setQuery] = useState({
    report: null,
    status: 'loading',
    loading: { table: false },
  });

  const clientFilters = useClientFilters();
  const projectFilters = useProjectFilters();

  const [params, setParams] = useState({
    period: null,
    invoiceServicesThroughPeriod: null,
    invoiceStatuses: [],
    invoiceSent: null,
    invoiceAccountingIntegrations: [],
    currency: workspace.currency,
    includeInvoicesWithoutPayments: false,
    sort: new QuerySort('project.name', 'asc'),
    ...clientFilters.filters,
    ...projectFilters.filters,
  });

  // Init and sync URL search params
  const searchParamsConfig = useSearchParamsConfig();
  const reportsSearchParamsConfig = useReportsSearchParamsConfig();
  const [searchParamsStatus, setSearchParamsStatus] = useState('pending');
  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        period: { ...reportsSearchParamsConfig.period, default: intervalOptions.all_dates },
        invoiceServicesThroughPeriod: reportsSearchParamsConfig.invoiceServicesThroughPeriod,
        invoiceStatuses: searchParamsConfig.invoiceStatuses,
        invoiceSent: searchParamsConfig.invoiceSent,
        invoiceAccountingIntegrations: searchParamsConfig.invoiceAccountingIntegrations,

        currency: searchParamsConfig.currency,
        includeInvoicesWithoutPayments: {
          default: false,
          serialize: (value) => (value === true ? 'true' : 'false'),
          deserialize: (value) => value === 'true',
        },
        sort: { default: new QuerySort('invoice.issuedOn', 'desc'), ...searchParamsConfig.sort },
        ...clientFilters.searchParamsConfig,
        ...projectFilters.searchParamsConfig,
      }),
      [searchParamsConfig, reportsSearchParamsConfig, clientFilters, projectFilters],
    ),

    onChange: (params) => setParams((state) => ({ ...state, ...params })),
  });

  // Map the values to perform the API query
  const urlSearchParams = useMemo(
    () => ({
      start: params.period?.start ?? undefined,
      end: params.period?.end ?? undefined,
      invoiceServicesThroughStart: params.invoiceServicesThroughPeriod?.start ?? undefined,
      invoiceServicesThroughEnd: params.invoiceServicesThroughPeriod?.end ?? undefined,
      invoiceStatusId: params.invoiceStatuses.map((v) => v.id),
      invoiceSent: params.invoiceSent ?? undefined,
      invoiceAccountingConnectionId: params.invoiceAccountingIntegrations?.map((v) => v.id) || undefined,
      currency: params.currency ?? undefined,
      includeInvoicesWithoutPayments: params.includeInvoicesWithoutPayments ? 'true' : undefined,
      sort: params.sort,
      ...clientFilters.mapUrlSearchParams(params),
      ...projectFilters.mapUrlSearchParams(params),
    }),
    [params, clientFilters, projectFilters],
  );

  useEffect(() => {
    if (searchParamsStatus === 'ready') return;
    searchParams.get().then((params) => {
      setParams((state) => ({ ...state, ...params }));
      setSearchParamsStatus('ready');
    });
  }, [searchParams, searchParamsStatus]);

  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).reports(report.id).data(urlSearchParams);
    setQuery((state) => ({
      ...state,
      data,
      status: 'ready',
      loading: { table: false },
    }));
  }, [api, workspace.id, urlSearchParams, report.id]);

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    fetchData();
  }, [fetchData, searchParamsStatus]);

  const [filtersVisible, setFiltersVisible] = useState(false);
  const showFilters = () => setFiltersVisible(true);
  const hideFilters = () => setFiltersVisible(false);
  const handleApplyFilters = (values) => {
    if (values !== params) setQuery((state) => ({ ...state, status: 'filtering' }));

    setParams({ ...params, ...values });
    searchParams.set(_.omit(values, 'sort'));
    hideFilters();
  };

  const handleSort = ({ column, sort }) => {
    setQuery((state) => ({ ...state, loading: { ...state.loading, table: true } }));
    const direction = column === sort.column && sort.direction === 'asc' ? 'desc' : 'asc';
    const querySort = new QuerySort(column, direction);
    setParams({ ...params, sort: querySort });
    searchParams.set({ sort: querySort });
  };

  const handleCurrencyChange = (currency) => {
    setQuery((state) => ({ ...state, loading: { ...state.loading, summary: true, table: true } }));
    setParams({ ...params, page: 0, currency });
    searchParams.set({ currency });
  };

  const confirmation = useConfirmation();

  const handleExport = (mimeType) => {
    confirmation.prompt((resolve) => (
      <ExportDialogAsync
        onLoad={api.www
          .workspaces(workspace.id)
          .reports(report.id)
          .data(urlSearchParams, {
            headers: { accept: mimeType },
          })}
        onClose={resolve}
      />
    ));
  };

  return (
    <Report>
      <Report.Header>
        <Report.Info report={report} />

        <Report.Actions>
          <SavedReportActions report={report} onSave={() => searchParams.set(params)} />
          <ExportDropdown>
            {({ setIsOpen }) => (
              <>
                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.csv);
                    setIsOpen(false);
                  }}>
                  Export to CSV
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.xlsx);
                    setIsOpen(false);
                  }}>
                  Export to Excel
                </ExportDropdown.Item>
              </>
            )}
          </ExportDropdown>

          <FilterButton isOutline onClick={showFilters} data-testid="filters_button" />
        </Report.Actions>
      </Report.Header>

      <Report.FiltersBar>
        <ReportPeriodFilter
          value={params.period}
          onChange={({ target: { value } }) => handleApplyFilters({ period: value })}
        />

        {features.multicurrency && (
          <CurrencyFilter value={params.currency} onChange={({ target: { value } }) => handleCurrencyChange(value)} />
        )}

        <YesNoFilter
          icon="filter"
          placeholder="Invoices without payments"
          value={params.includeInvoicesWithoutPayments ? 'yes' : 'no'}
          onChange={({ target: { value } }) => handleApplyFilters({ includeInvoicesWithoutPayments: value === 'yes' })}
        />

        <ClientFiltersBar filters={params} onChange={handleApplyFilters} />
        <ProjectFiltersBar filters={params} onChange={handleApplyFilters} />
        <InvoiceFiltersBar filters={params} onChange={handleApplyFilters} />
      </Report.FiltersBar>

      {(() => {
        switch (query.status) {
          case 'loading':
          case 'filtering':
            return <PageLoader />;

          default:
            return (
              <>
                <Data query={query} params={params} onSort={handleSort} onCurrencyChange={handleCurrencyChange} />
                <Filters values={params} isOpen={filtersVisible} onApply={handleApplyFilters} onClose={hideFilters} />
              </>
            );
        }
      })()}
    </Report>
  );
}

function Data({ query, params, onSort }) {
  const report = query.data;
  const { workspace } = useWorkspace();

  return (
    <Report.Table>
      <Table>
        <Table.Header sticky>
          <Table.Column sticky width="8rem" name="invoice.transactionNumber" onSort={onSort} sort={params.sort}>
            #
          </Table.Column>
          <Table.Column name="client.name" minWidth="16rem" onSort={onSort} sort={params.sort}>
            Client
          </Table.Column>
          <Table.Column minWidth="16rem">Projects</Table.Column>
          <Table.Column width="8rem" name="invoice.issuedOn" onSort={onSort} sort={params.sort}>
            Issued
          </Table.Column>
          <Table.Column width="8rem" align="right" name="invoice.dueOn" onSort={onSort} sort={params.sort}>
            Due
          </Table.Column>
          <Table.Column width="6rem" name="actualStatusId" onSort={onSort} sort={params.sort}>
            Status
          </Table.Column>
          <Table.Column width="10rem" align="right" name="convertedTotal" onSort={onSort} sort={params.sort}>
            Amount
          </Table.Column>
          <Table.Column width="10rem" align="right">
            Total Payments
          </Table.Column>
        </Table.Header>

        <Table.Body fade={query.loading.table}>
          {report.records.map((invoice) => {
            const { payments } = invoice;

            return (
              <React.Fragment key={invoice.id}>
                <Table.GroupRow top="3rem">
                  <Table.Cell>
                    <RouteLink to={`/app/${workspace.key}/billing/invoices/${invoice.id}`}>{invoice.number}</RouteLink>
                  </Table.Cell>
                  <Table.Cell>
                    <ClientLink client={invoice.client} />
                  </Table.Cell>
                  <Table.Cell>
                    <FirstProject invoice={invoice} />
                    <Projects invoice={invoice} />
                  </Table.Cell>
                  <Table.Cell>
                    <DateTime value={invoice.issuedOn} />
                    {invoice?.sentAt && <SentIndicator sentAt={invoice.sentAt} />}
                  </Table.Cell>
                  <Table.Cell>
                    <div style={invoice.isLate ? { color: colors.danger } : undefined}>
                      <DateTime value={invoice.dueOn} />
                      {invoice.isLate && <p>({pluralize('day', invoice.daysLate, true)})</p>}
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    {invoice.status.name}
                    {invoice.accountingSynced && <InvoiceSyncIndicator invoice={invoice} />}
                  </Table.Cell>
                  <Table.Cell>
                    <p>
                      <Currency value={invoice.convertedTotal} currency={report.currency} />
                      {invoice.currency !== report.currency && (
                        <Byline>
                          <Currency value={invoice.total} currency={invoice.currency} />
                        </Byline>
                      )}
                    </p>
                  </Table.Cell>
                  <Table.Cell>
                    <p>
                      <Currency value={invoice.convertedTotal - invoice.convertedBalance} currency={report.currency} />
                      {invoice.currency !== report.currency && (
                        <Byline>
                          <Currency value={invoice.total - invoice.balance} currency={invoice.currency} />
                        </Byline>
                      )}
                    </p>
                  </Table.Cell>
                </Table.GroupRow>

                {payments.length > 0 && (
                  <DetailTable style={{ fontSize: '.75rem' }}>
                    <DetailTable.Header style={{ background: colors.white }}>
                      <DetailTable.Column width="2rem"></DetailTable.Column>
                      <DetailTable.Column width="35rem">Date</DetailTable.Column>
                      <DetailTable.Column width="28.5rem">Ref #</DetailTable.Column>
                      <DetailTable.Column>Payment Method</DetailTable.Column>
                      <DetailTable.Column align="right" width="7.5rem">
                        Amount
                      </DetailTable.Column>
                    </DetailTable.Header>

                    <DetailTable.Body>
                      {payments.map((payment) => {
                        return (
                          <DetailTable.Row key={payment.clientPaymentId}>
                            <DetailTable.Cell />
                            <DetailTable.Cell>
                              <DateTime value={payment.receivedOn} />
                            </DetailTable.Cell>
                            <DetailTable.Cell>{payment.referenceNumber}</DetailTable.Cell>
                            <DetailTable.Cell>{payment.paymentMethodName}</DetailTable.Cell>
                            <DetailTable.Cell>
                              <p>
                                <Currency value={payment.convertedAmount} currency={report.currency} />
                                {payment.currency !== report.currency && (
                                  <Byline>
                                    <Currency value={payment.amount} currency={payment.currency} />
                                  </Byline>
                                )}
                              </p>
                            </DetailTable.Cell>
                          </DetailTable.Row>
                        );
                      })}
                    </DetailTable.Body>
                  </DetailTable>
                )}
              </React.Fragment>
            );
          })}

          <Table.Row style={{ fontWeight: weights.bold }}>
            <Table.Cell>Total</Table.Cell>
            <Table.Cell></Table.Cell>
            <Table.Cell></Table.Cell>
            <Table.Cell></Table.Cell>
            <Table.Cell></Table.Cell>
            <Table.Cell></Table.Cell>
            <Table.Cell>
              <Currency value={report.totals.total} currency={report.currency} />
            </Table.Cell>
            <Table.Cell>
              <Currency value={report.totals.payments} currency={report.currency} />
            </Table.Cell>
          </Table.Row>
        </Table.Body>

        <Table.Status
          total={report.records.length}
          label="Invoice"
          isLoading={query.status !== 'ready'}
          partial={report.partialResults}
        />
      </Table>
    </Report.Table>
  );
}

function Filters({ values, isOpen, onClose, onApply }) {
  const [filters, setFilters] = useState(values);

  const handleApply = () => {
    onApply(filters);
  };

  const handleFilter = (filter) => {
    setFilters({ ...filters, ...filter });
  };

  const handleCancel = () => {
    setFilters(values);
    onClose();
  };

  useEffect(() => {
    setFilters(values);
  }, [values]);

  return (
    <Report.FiltersDrawer isOpen={isOpen} onApply={handleApply} onClose={handleCancel}>
      <YesNoFilter
        icon="filter"
        placeholder="Include invoices without payments"
        value={filters.includeInvoicesWithoutPayments ? 'yes' : 'no'}
        onChange={({ target: { value } }) => handleFilter({ includeInvoicesWithoutPayments: value === 'yes' })}
      />

      <ReportPeriodFilter
        value={filters.period}
        onChange={({ target: { value } }) => handleFilter({ period: value })}
      />

      <ClientFiltersGroup filters={filters} onChange={handleFilter} />

      <ProjectFiltersGroup filters={filters} onChange={handleFilter} />

      <InvoiceFiltersGroup filters={filters} onChange={handleFilter} />
    </Report.FiltersDrawer>
  );
}

const FirstProject = ({ invoice }) => {
  const project = invoice.projects[0];
  if (!project) return null;

  return <ProjectLink project={project} client={invoice.client} />;
};

const Title = styled.p`
  color: ${colors.grey40};
  font-size: 0.75rem;
  font-weight: ${weights.black};
  letter-spacing: 0.0625rem;
  text-transform: uppercase;
  margin-bottom: 0.5rem;
  margin-left: 0.25rem;
`;

const Projects = ({ invoice }) => {
  let projectsCount = invoice.projects.length - 1; // Remove the first project because it already shows a tag
  if (projectsCount <= 0) return null;

  return (
    <Tooltip
      placement="right"
      message={
        <div style={{ fontSize: '1rem' }}>
          <Title>Projects</Title>

          {invoice.projects.map((projects) => (
            <Tag style={{ backgroundColor: colors.grey5 }} key={projects.id}>
              <small>{projects.name}</small>
            </Tag>
          ))}
        </div>
      }>
      <Tag style={{ backgroundColor: colors.grey5, color: colors.grey40, cursor: 'default' }}>
        <small>+{projectsCount}</small>
      </Tag>
    </Tooltip>
  );
};
